import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from '@digital-twins/design-system/icons'
import type { KnowledgeDatasetPreview } from '@digital-twins/shared-types/knowledge-dataset-preview'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { knowledgeApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { DatasetTablePreviewProps } from '@/types/routes/dataset-preview'

const ROW_LIMIT = 50
const COLUMN_LIMIT = 20

/** One bounded source window at a time, never the whole file in the browser. */
export function DatasetTablePreview({ documentToken, title, className }: DatasetTablePreviewProps) {
  const { t } = useTranslation('app')
  const [sheetName, setSheetName] = useState<string | undefined>()
  const [rowOffset, setRowOffset] = useState(0)
  const [columnOffset, setColumnOffset] = useState(0)
  const [page, setPage] = useState<KnowledgeDatasetPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    setLoading(true)
    setError(false)
    // Clear the old window immediately: it must never appear under new offsets.
    setPage(null)
    void knowledgeApi.getDatasetPreview(documentToken, {
      rowOffset, columnOffset, rowLimit: ROW_LIMIT, columnLimit: COLUMN_LIMIT, sheetName,
    }, controller.signal).then(result => {
      if (!cancelled) setPage(result)
    }).catch(() => {
      if (!cancelled) setError(true)
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true; controller.abort() }
  }, [documentToken, sheetName, rowOffset, columnOffset, retry])

  function pager(axis: 'rows' | 'columns') {
    const rows = axis === 'rows'
    const offset = rows ? (page?.rowOffset ?? rowOffset) : (page?.columnOffset ?? columnOffset)
    const total = rows ? (page?.totalRows ?? 0) : (page?.totalColumns ?? 0)
    const size = rows ? (page?.rowLimit ?? ROW_LIMIT) : (page?.columnLimit ?? COLUMN_LIMIT)
    const shown = rows ? (page?.rows.length ?? 0) : (page?.columns.length ?? 0)
    const setOffset = rows ? setRowOffset : setColumnOffset
    const last = Math.max(0, Math.floor((total - 1) / size) * size)
    return (
      <div className="flex flex-wrap items-center gap-2" aria-label={t(`library.datasetPreview.${axis}`)}>
        <Text size="xs" color="muted">
          {t(`library.datasetPreview.${axis}Range`, { start: shown ? offset + 1 : 0, end: shown ? offset + shown : 0, total })}
        </Text>
        {([
          { key: 'first', icon: ChevronFirst, value: 0, disabled: offset === 0 },
          { key: 'previous', icon: ChevronLeft, value: Math.max(0, offset - size), disabled: offset === 0 },
          { key: 'next', icon: ChevronRight, value: offset + size, disabled: offset + shown >= total },
          { key: 'last', icon: ChevronLast, value: last, disabled: offset >= last },
        ] as const).map(({ key, icon: Icon, value, disabled }) => (
          <Button key={key} type="button" variant="ghost" size="icon" className="h-8 w-8"
            aria-label={t(`library.datasetPreview.${axis}_${key}`)}
            disabled={loading || error || disabled} onClick={() => setOffset(value)}>
            <Icon className="h-4 w-4" />
          </Button>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col gap-3', className)} data-testid="dataset-table-preview">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <Text size="sm">{title}</Text>
        {page && page.sheets.length > 1 && (
          <select className="max-w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            aria-label={t('library.datasetPreview.sheet')} value={page.sheetName ?? ''}
            onChange={event => { setSheetName(event.target.value); setRowOffset(0); setColumnOffset(0) }}>
            {page.sheets.map(sheet => <option key={sheet} value={sheet}>{sheet}</option>)}
          </select>
        )}
      </div>
      {error ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6" role="alert">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <Text size="sm">{t('library.datasetPreview.error')}</Text>
          <Button variant="outline" onClick={() => setRetry(value => value + 1)}>{t('library.datasetPreview.retry')}</Button>
        </div>
      ) : loading ? (
        <div className="flex flex-1 items-center justify-center rounded-xl border border-border bg-muted/30" role="status">
          <Text size="sm" color="muted">{t('library.datasetPreview.loading')}</Text>
        </div>
      ) : page && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
            {pager('columns')}
          </div>
          <div className="min-h-0 min-w-0 flex-1 overflow-auto" tabIndex={0} aria-label={t('library.datasetPreview.table')}>
            <table className="w-max min-w-full border-collapse text-left text-xs" data-testid="dataset-preview-table">
              <thead className="sticky top-0 z-10 bg-muted">
                <tr>
                  <th className="border-b border-r border-border px-3 py-2" scope="col">#</th>
                  {page.columns.map((column, index) => (
                    <th key={page.columnOffset + index} scope="col" className="max-w-80 border-b border-r border-border px-3 py-2 font-medium"
                      title={column.label ? `${column.name}: ${column.label}` : column.name}>
                      <span className="block max-w-80 truncate">{column.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row, index) => (
                  <tr key={page.rowOffset + index} className="hover:bg-muted/40">
                    <th scope="row" className="border-b border-r border-border px-3 py-2 text-right font-normal tabular-nums text-muted-foreground">{page.rowOffset + index + 1}</th>
                    {page.columns.map((_, cell) => (
                      <td key={page.columnOffset + cell} className="max-w-80 truncate border-b border-r border-border px-3 py-2" title={row[cell] ?? ''}>
                        {row[cell] ?? <span className="text-muted-foreground" aria-label={t('library.datasetPreview.emptyCell')}>·</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {!page.rows.length && <div className="p-6"><Text size="sm" color="muted">{t('library.datasetPreview.empty')}</Text></div>}
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border px-3 py-2">
            {pager('rows')}
            {page.truncatedCellCount > 0 && <Text size="xs" color="muted">{t('library.datasetPreview.shortened', { count: page.truncatedCellCount })}</Text>}
          </div>
        </div>
      )}
    </div>
  )
}
